
import React from 'react';
import { Button } from "@/components/ui/button";
import { 
  Film, 
  Settings, 
  Search,
  Plus,
  ArrowLeft,
  Download,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Project } from '@/hooks/useProjects';

interface HeaderProps {
  project: Project;
  exportStatus: string | null;
  exportStatusMessage: string | null;
  isExporting: boolean;
  onExport: () => void;
  onDownload: () => void;
}

const Header = ({
  project,
  exportStatus,
  exportStatusMessage,
  isExporting,
  onExport,
  onDownload
}: HeaderProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleBackToDashboard = () => {
    navigate('/studio');
  };

  const handleRefreshStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['project', project.id] });
  };

  const renderExportButton = () => {
    if (exportStatus === 'completed' && project.exported_video_storage_path) {
      return (
        <Button onClick={onDownload} size="sm" className="bg-green-500 hover:bg-green-600">
          <Download className="h-4 w-4 mr-2" />
          Download Video
        </Button>
      );
    }

    if (exportStatus === 'processing' || exportStatus === 'queued' || isExporting) {
      return (
        <Button disabled size="sm" className="bg-editor-accent">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {exportStatus === 'queued' ? 'Queued' : 'Processing...'}
        </Button>
      );
    }

    if (exportStatus === 'failed') {
      return (
        <Button onClick={onExport} size="sm" variant="destructive">
          <AlertTriangle className="h-4 w-4 mr-2" />
          Retry Export
        </Button>
      );
    }

    return (
      <Button onClick={onExport} disabled={isExporting} size="sm" className="bg-editor-accent hover:bg-editor-highlight">
        <Film className="h-4 w-4 mr-2" />
        Export Video
      </Button>
    );
  };

  const renderStatusMessage = () => {
    if (!exportStatusMessage) return null;

    let icon;
    let textColor = "text-muted-foreground";

    switch (exportStatus) {
      case 'completed':
        icon = <CheckCircle className="h-4 w-4 text-green-500" />;
        textColor = "text-green-400";
        break;
      case 'failed':
        icon = <AlertTriangle className="h-4 w-4 text-red-500" />;
        textColor = "text-red-400";
        break;
      case 'processing':
      case 'queued':
        icon = <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
        textColor = "text-blue-400";
        break;
      default:
        return null;
    }

    return (
      <div className="flex items-center text-sm">
        {icon}
        <span className={`ml-2 ${textColor}`}>{exportStatusMessage}</span>
      </div>
    );
  }

  return (
    <header className="border-b border-editor-light py-3 px-4 flex items-center justify-between bg-editor-medium">
      <div className="flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackToDashboard}
          className="mr-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Film className="h-6 w-6 text-editor-accent mr-2" />
        <div>
          <h1 className="text-xl font-bold">{project.title}</h1>
          {project.description && (
            <p className="text-sm text-muted-foreground">{project.description}</p>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        {renderStatusMessage()}
        <div className="flex items-center space-x-2">
          {renderExportButton()}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefreshStatus}
            className="text-muted-foreground hover:text-foreground"
            title="Refresh export status"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Button size="sm" variant="outline" className="border-editor-light">
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </Button>
      </div>
    </header>
  );
};

export default Header;
